import { getFeatureFlagsControllerGetFlagsQueryKey } from '@artic/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { partnerDetailQueryOptions } from '../partners/queries';
import { useRealtimeSocket } from './realtime-provider';

interface UsePartnerRealtimeSyncOptions {
  /** Feature-flag gate. When false the hook no-ops and opens no socket. */
  enabled: boolean;
}

/**
 * Joins `partner:<partnerId>` once and, on a `partner_changed` signal, refetches
 * the partner's feature flags (so the sidebar nav updates live as flags flip)
 * and the admin partner-detail record (if that page is open). No presence —
 * this topic carries no data, only a change signal. Shares the ref-counted
 * socket with `useProjectChannel` via the `RealtimeProvider`.
 */
export function usePartnerRealtimeSync(
  partnerId: string | undefined,
  { enabled }: UsePartnerRealtimeSyncOptions,
): void {
  const queryClient = useQueryClient();
  const { acquireSocket, releaseSocket } = useRealtimeSocket();

  useEffect(() => {
    if (!enabled || !partnerId) return;

    const socket = acquireSocket();
    if (!socket) return;

    const chan = socket.channel(`partner:${partnerId}`, {});
    const ref = chan.on('partner_changed', () => {
      // Nav flags — the exact query key the FeatureFlagProvider's suspense query
      // reads, so this is a background refetch (no suspense-fallback flash).
      void queryClient.invalidateQueries({
        queryKey: getFeatureFlagsControllerGetFlagsQueryKey({ partner_id: partnerId }),
      });
      // Admin partner-detail record, refreshed if the detail page is open.
      void queryClient.invalidateQueries({
        queryKey: partnerDetailQueryOptions(partnerId).queryKey,
      });
    });

    chan.join();

    return () => {
      chan.off('partner_changed', ref);
      chan.leave();
      releaseSocket();
    };
  }, [enabled, partnerId, acquireSocket, releaseSocket, queryClient]);
}
